import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";
import { Loader2, Plus, Trash2, Save, CalendarDays, Eraser, SlidersHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NameCleaner } from "@/components/NameCleaner";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MultiFilter, inSel } from "@/components/MultiFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLORS = ["#9E7B41", "#0F172A", "#c9a227", "#64748B", "#B45309", "#0E7490"];

const agg = (rows, keyFn, years, pct = false) => {
  const m = new Map();
  rows.forEach((r) => {
    const k = (keyFn(r) || "—").toString().trim() || "—";
    const o = m.get(k) || { name: k, total: 0 };
    o[r.year] = (o[r.year] || 0) + 1; o.total += 1;
    m.set(k, o);
  });
  const per = Object.fromEntries(years.map((y) => [y, rows.filter((r) => r.year === y).length]));
  return [...m.values()].sort((a, b) => b.total - a.total).map((o) => {
    years.forEach((y) => { o[y] = pct ? (per[y] ? Math.round(((o[y] || 0) / per[y]) * 100) : 0) : o[y] || 0; });
    return o;
  });
};

const DIMS = [["salon", "Salon"], ["sector", "Secteur"], ["seller", "Vendeur"], ["gamme", "Gamme"], ["model", "Modèle"], ["modelv", "Modèle et version"],
  ["brand", "Marque reprise"], ["rep", "Reprise"], ["ret", "Salon / retombée"], ["src", "Origine"]];
const GLOBAL = DIMS.filter(([k]) => !["salon", "modelv", "ret"].includes(k));
const gval = (r, k) => (k === "rep" ? (r.reprise ? "Avec reprise" : "Sans reprise") : k === "src" ? (r.source === "excel" ? "Historique Excel" : "Appli")
  : k === "ret" ? (r.retombee ? "Retombée" : "Pendant le salon") : k === "modelv" ? [r.model, r.version].filter(Boolean).join(" ") || "—" : (r[k] || "—"));

const ChartFilters = ({ rows, f, setF, testid }) => {
  const n = Object.values(f).filter((s) => s != null).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant={n ? "default" : "outline"} className="h-8 gap-1 text-xs" data-testid={`${testid}-filters`}><SlidersHorizontal className="w-3.5 h-3.5" /> Filtres{n ? ` (${n})` : ""}</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2" data-testid={`${testid}-filters-panel`}>
        {DIMS.filter(([k]) => k !== "modelv").map(([k, l]) => {
          const vals = [...new Set(rows.map((r) => gval(r, k)))].sort((a, b) => a.localeCompare(b, "fr"));
          return <MultiFilter key={k} label={l} sel={f[k] ?? null} onChange={(s) => setF((x) => ({ ...x, [k]: s }))} testid={`${testid}-f-${k}`} className="w-full h-8 text-xs"
            options={vals.map((v) => ({ value: v, label: `${v} (${rows.filter((r) => gval(r, k) === v).length})` }))} />;
        })}
        {n > 0 && <Button size="sm" variant="ghost" className="text-xs underline w-full" onClick={() => setF({})} data-testid={`${testid}-f-reset`}>Réinitialiser</Button>}
      </PopoverContent>
    </Popover>
  );
};

const ChartCard = ({ title, testid, rows, by: by0, years, pct = false, top = Infinity, height = 300 }) => {
  const [sel, setSel] = useState(null);
  const [by, setBy] = useState(by0);
  const [f, setF] = useState({});
  const fr = rows.filter((r) => DIMS.every(([k]) => f[k] == null || inSel(f[k], gval(r, k))));
  const all = agg(fr, (r) => gval(r, by), years, pct);
  const data = all.filter((d) => inSel(sel, d.name)).slice(0, top);
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-bold text-sm uppercase tracking-wide" data-testid={`${testid}-title`}>{by === by0 ? title : `${pct ? "Répartition" : "Commandes"} par ${DIMS.find(([k]) => k === by)[1].toLowerCase()}`}</h3>
        <div className="flex flex-wrap items-center gap-1.5">
          <Select value={by} onValueChange={(v) => { setBy(v); setSel(null); }}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`${testid}-by`}><span className="text-muted-foreground mr-1">Par :</span><SelectValue /></SelectTrigger>
            <SelectContent>{DIMS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <ChartFilters rows={rows} f={f} setF={setF} testid={testid} />
          {(all.length > 1 || sel !== null) && <MultiFilter label="Afficher" sel={sel} onChange={setSel} testid={`${testid}-filter`} className="w-40 h-8 text-xs"
            options={all.map((d) => ({ value: d.name, label: pct ? d.name : `${d.name} (${d.total})` }))} />}
        </div>
      </div>
      <Chart data={data} years={years} pct={pct} height={height} />
    </div>
  );
};

const Chart = ({ data, years, pct = false, height = 300 }) => (
    <div className="overflow-x-auto">
    {data.length === 0 ? <div className="text-sm text-muted-foreground py-10 text-center">Aucune commande</div> : (
      <ResponsiveContainer width={data.length > 15 ? data.length * (20 + years.length * 14) : "100%"} height={height}>
        <BarChart data={data} margin={{ top: 16, right: 8, left: -16, bottom: data.length > 5 ? 60 : 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" interval={0} tick={{ fontSize: 10 }} angle={data.length > 5 ? -35 : 0} textAnchor={data.length > 5 ? "end" : "middle"} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} unit={pct ? "%" : ""} />
          <Tooltip formatter={(v) => (pct ? `${v} %` : v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="top" />
          {years.map((y, i) => (
            <Bar key={y} dataKey={y} name={String(y)} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]}>
              <LabelList dataKey={y} position="top" fontSize={9} formatter={(v) => (v ? (pct ? `${v}%` : v) : "")} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    )}
    </div>
);

const SalonDates = ({ onSaved }) => {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/salons").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  const set = (i, k, v) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/salons", items.filter((x) => x.name && x.start)); setItems(data); toast.success("Dates des salons enregistrées"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  if (!items) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-6" data-testid="salon-dates">
      <div className="flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4 text-primary" /><h3 className="font-display font-bold text-sm uppercase">Dates des salons</h3></div>
      <p className="text-xs text-muted-foreground mb-3">Commande signée pendant le salon = salon ; devis fait pendant le salon et signé après = retombée. Si des promotions sont indiquées, seules les commandes portant l'une d'elles comptent.</p>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`salon-row-${i}`}>
            <Input value={s.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Nom du salon" className="w-56" data-testid={`salon-name-${i}`} />
            <Input type="date" value={s.start} onChange={(e) => set(i, "start", e.target.value)} className="w-40" data-testid={`salon-start-${i}`} />
            <span className="text-xs text-muted-foreground">au</span>
            <Input type="date" value={s.end} onChange={(e) => set(i, "end", e.target.value)} className="w-40" data-testid={`salon-end-${i}`} />
            <Input value={s.promo || ""} onChange={(e) => set(i, "promo", e.target.value)} placeholder="Promotions (facultatif, séparées par des virgules)" className="w-72" data-testid={`salon-promo-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} data-testid={`salon-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setItems((xs) => [...xs, { name: "", start: "", end: "", promo: "" }])} data-testid="salon-add"><Plus className="w-4 h-4" /> Ajouter un salon</Button>
        <Button size="sm" className="gap-1" onClick={save} disabled={saving} data-testid="salon-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</Button>
      </div>
    </div>
  );
};

export default function SalonStats() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [salons, setSalons] = useState(null);
  const [years, setYears] = useState(undefined);
  const [withRet, setWithRet] = useState(true);
  const [noStock, setNoStock] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [gf, setGf] = useState({});
  const load = () => api.get("/stats/salons").then(({ data }) => setRows(data)).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Statistiques indisponibles"); setRows([]); });
  useEffect(() => { load(); }, []);
  const allYears = useMemo(() => [...new Set((rows || []).map((r) => r.year))].sort(), [rows]);
  const selYears = years === undefined ? allYears.slice(-2) : years === null ? allYears : years;
  const pre = (rows || []).filter((r) => inSel(salons, r.salon) && selYears.includes(r.year) && (!noStock || !r.stock));
  const base = pre.filter((r) => GLOBAL.every(([k]) => gf[k] == null || inSel(gf[k], gval(r, k))));
  const sel = base.filter((r) => withRet || !r.retombee);
  const ys = selYears;
  if (!rows) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="salon-stats-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-1">Statistiques des salons</h1>
      <p className="text-sm text-muted-foreground mb-6">Commandes de l'historique Excel (2022 → 2026) et commandes signées dans l'appli pendant les salons.</p>
      {user?.role === "admin" && <SalonDates onSaved={load} />}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <MultiFilter label="Salon" sel={salons} onChange={setSalons} testid="stats-salon-filter" className="w-56"
          options={[...new Set(rows.map((r) => r.salon))].sort().map((s) => ({ value: s, label: `${s} (${rows.filter((r) => r.salon === s).length})` }))} />
        <MultiFilter label="Années" sel={years === undefined ? allYears.slice(-2) : years} onChange={setYears} testid="stats-year-filter" className="w-44"
          options={allYears.map((y) => ({ value: y, label: String(y) }))} />
        <Button variant={withRet ? "default" : "outline"} onClick={() => setWithRet((v) => !v)} aria-pressed={withRet} data-testid="stats-retombees">Avec retombées</Button>
        <Button variant={noStock ? "default" : "outline"} onClick={() => setNoStock((v) => !v)} aria-pressed={noStock} data-testid="stats-client-only">Commandes client uniquement</Button>
        <span className="text-sm text-muted-foreground" data-testid="stats-count"><b className="text-foreground">{sel.length}</b> commandes</span>
        {user?.role === "admin" && <Button variant="outline" className="gap-1 ml-auto" onClick={() => setCleaning(true)} data-testid="stats-clean-names"><Eraser className="w-4 h-4" /> Nettoyer les noms</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6 -mt-3" data-testid="stats-global-filters">
        <span className="text-xs font-bold uppercase text-muted-foreground">Filtres pour tous les graphiques</span>
        {GLOBAL.map(([k, l]) => {
          const vals = [...new Set(pre.map((r) => gval(r, k)))].sort((a, b) => a.localeCompare(b, "fr"));
          return <MultiFilter key={k} label={l} sel={gf[k] ?? null} onChange={(s) => setGf((f) => ({ ...f, [k]: s }))} testid={`stats-global-${k}`} className="w-auto max-w-[15rem] h-8 text-xs"
            options={vals.map((v) => ({ value: v, label: `${v} (${pre.filter((r) => gval(r, k) === v).length})` }))} />;
        })}
        {Object.values(gf).some((s) => s != null) && <Button size="sm" variant="ghost" className="text-xs underline" onClick={() => setGf({})} data-testid="stats-global-reset">Réinitialiser les filtres</Button>}
      </div>
      {user?.role === "admin" && <NameCleaner open={cleaning} onOpenChange={setCleaning} onChanged={load} />}
      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Commandes totales avec retombées" testid="chart-total-with" years={ys} rows={base} by="salon" />
        <ChartCard title="Commandes totales hors retombées" testid="chart-total-without" years={ys} rows={base.filter((r) => !r.retombee)} by="salon" />
        <ChartCard title="Commandes par secteur" testid="chart-sector" years={ys} rows={sel} by="sector" height={340} />
        <ChartCard title="Commandes par vendeur" testid="chart-seller" years={ys} rows={sel} by="seller" height={340} />
        <ChartCard title="Commandes par gamme" testid="chart-gamme" years={ys} rows={sel} by="gamme" />
        <ChartCard title="Commandes par modèle" testid="chart-model" years={ys} rows={sel} by="model" height={340} />
        <div className="lg:col-span-2"><ChartCard title="Commandes par modèle et par version" testid="chart-model-version" years={ys} rows={sel} by="modelv" top={30} height={380} /></div>
        <ChartCard title="Taux de reprise" testid="chart-reprise" years={ys} rows={sel} by="rep" pct />
        <ChartCard title="Marques reprises" testid="chart-brands" years={ys} rows={sel.filter((r) => r.reprise)} by="brand" />
      </div>
    </div>
  );
}
